import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../Components/Themed"
import { LeadersApi } from './api';
import type { CategoryProps, LeaderProps, PublicPlayerProps, TimerangeProps } from '../types';
import { ActivityIndicator, FlatList } from 'react-native';
import { Checkbox, Icons, Toggle } from '../Components';
import LeaderCard from './components/LeaderCard';
import Pagination from '../Components/Pagination';
import { useColors } from '../constants/useColors';

type LeaderboardProps = {
    player_id?:string,
    stat?:string,
    range_days?:number,
    onSelectLeader:(l:LeaderProps, p:PublicPlayerProps) => void
}
const sections = ['header','timeranges', 'my_leader', 'pagination', 'leaders']
const Leaderboard = ({ player_id, stat, range_days, onSelectLeader }:LeaderboardProps) => {
    const Colors = useColors();
    const [ static_data, setStaticData ] = useState<{
        time_ranges:TimerangeProps[],
        categories: CategoryProps[],
        show_categories:boolean
    }>({
        time_ranges:[],
        categories:[],
        show_categories:false
    });
    const { time_ranges, categories, show_categories } = static_data;
    const [ my_data, setMyData ] = useState<{
        me?:PublicPlayerProps,
        my_leader?:LeaderProps
    }>({});
    const { me, my_leader } = my_data;
    const [ leaders_data, setLeadersData ] = useState<{
        loading:boolean,
        category_id?:string,
        timerange_id?:string
        leaders:LeaderProps[],
        players:PublicPlayerProps[],
        offset:number
    }>({
        loading:false,
        leaders: [],
        players:[],
        offset:0
    });
    const { loading, leaders, players, timerange_id, category_id, offset } = leaders_data;
    const timerange = time_ranges.find(t => t.timerange_id == timerange_id);
    const category = categories.find(c => c.category_id == category_id);
    useEffect(() => {
        LeadersApi.setEnvironment();
        start_up(stat)
    },[stat, range_days, player_id]);

    const start_up = async(stat?:string, range_days?:number) => {
        let data = await getStaticData();
        let category = data.categories.find(c => c.stat == stat);
        if(!category){ category = data.categories[0] }

        let range = data.time_ranges.find(r => r.range_days == range_days);
        if(!range){ range = data.time_ranges[0] }

        if(!category || !range){ return alert('Unable to load leaderboard')}
        getLeaders(category.category_id, range.timerange_id, 0);
    }

    const getLeaders = async(category_id:string, timerange_id: string, page:number) => {
        setLeadersData({ ...leaders_data, loading:true });
        const resp = await LeadersApi.getLeaders(category_id, timerange_id, page, player_id);
        if(!resp){
            setLeadersData({ ...leaders_data, loading:false });
            return alert('Unable to get leaderboard')
        }
        const player_ids = resp.leaders.map(l => l.player_id);
        const players = await LeadersApi.getPlayersByPlayerIds(player_ids);

        if(page == 0 && player_id){
            let me_player = players.find(p => p.player_id == player_id);
            let me_leader = resp.leaders.find(l => l.player_id == player_id);
            setMyData({ me:me_player, my_leader: me_leader });
        }

        setLeadersData({
            ...leaders_data,
            loading:false,
            category_id,
            timerange_id,
            leaders:resp.leaders,
            offset: page,
            players
        });
    }

    const getStaticData = async():Promise<{ categories:CategoryProps[], time_ranges:TimerangeProps[] }> => {
        //Start Loading
        const resp = await LeadersApi.getCategories();
        setStaticData({ categories: resp.categories, time_ranges:resp.time_ranges, show_categories:false });
        return resp
    }
    
    const renderLeaders = (data: { item: LeaderProps, index:number }) => {
        if(!category){ return <></> }
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        return (
            <View transparent style={{ margin:4 }}>
                <LeaderCard
                    leader={data.item}
                    player={player}
                    category={category}
                    onSelectLeader={onSelectLeader}
                />
            </View>
        )
    }

    const renderCategories = (data: {item:CategoryProps, index:number} ) => {
        if(!timerange_id){ return <></> }
        const checked = category_id == data.item.category_id ? true : false
        return (
            <Button style={{ flexDirection:'row', alignItems:'center', padding:15, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => {
                getLeaders(data.item.category_id, timerange_id, 0);
                setStaticData({ ...static_data, show_categories: false })
            }}>
                <Checkbox color={Colors.text.action} disabled fill_style={{ borderRadius:100 }} style={{ borderRadius:100 }} checked={checked} onSelect={() => console.log('')} />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='action'>{data.item.name}</Text>
                </View>
            </Button>
        )
    }

    const renderSections = (data:{item:string, index:number}) => {
        switch(data.item){
            case 'header':
                if(!category || !timerange){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:15 }}>
                            <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Leaders by {category.name}</Text>
                                <Text theme='description' style={{ marginTop:3 }}>{timerange.name} ({timerange.order_minimum} Bets Minimum)</Text>
                            </View>
                            
                            <Button float style={{ flexDirection:'row', padding:15, alignItems:'center' }} onPress={() => setStaticData({ ...static_data, show_categories: !show_categories })}>
                                <Text theme='action' size={14} style={{ marginRight:5 }}>{category?.name ?? 'Select Category'}</Text>
                                <Icons.ChevronIcon size={8} direction={show_categories ? 'up' : 'down'} color={Colors.text.action}/>
                            </Button>

                        </View>
                        {show_categories ?
                        <View>
                            <FlatList
                                data={categories}
                                key={'category_list'}
                                keyExtractor={(item) => item.category_id.toString()}
                                renderItem={renderCategories}
                            />
                        </View>
                        :<></>}
                    </View>

                )

            case 'timeranges':
                return (
                    <View style={{ padding:5 }}>
                        <Toggle
                            options={time_ranges.map(c => { return { key: c.range_days.toString(), label: c.name } })}
                            selected_option={timerange?.range_days?.toString()}
                            onSelectOption={(option) => {
                                let selected_range = time_ranges.find(c => c.range_days.toString() == option);
                                if(!selected_range || !category_id){ return }
                                getLeaders(category_id, selected_range.timerange_id, 0);
                            }}
                        />
                    </View>
                )
            case 'pagination':
                if(!category_id || !timerange_id){ return <></> }
                return (
                    <View>
                        <Pagination
                            offset={offset}
                            onNext={() => getLeaders(category_id, timerange_id, offset + 1)}
                            onPrevious={() => getLeaders(category_id, timerange_id, offset - 1)}
                        />
                    </View>
                )
            case 'my_leader':
                if(!my_leader){ return <></> }
                if(!me){ return <></> }
                if(!category){ return <></> }
                return (
                    <View type='header' style={{ padding:10 }}>
                        <Text style={{ marginBottom:10 }} theme='h1'>Your Place</Text>
                        <LeaderCard
                            leader={my_leader}
                            player={me}
                            category={category}
                            onSelectLeader={(l, p) => console.log(l,p)}
                        />
                    </View>
                )
            case 'leaders':
                return (
                    <View style={{ padding:10 }}>
                        {loading ?
                        <ActivityIndicator
                            style={{ padding:10, alignSelf: 'center' }}
                            size={'large'}
                            color={Colors.text.h1}
                        />
                        :<></>}
                        <FlatList
                            data={leaders.sort((a,b) => a.place - b.place)}
                            key={'leaders_list'}
                            renderItem={renderLeaders}
                            keyExtractor={(item) => item.leader_id.toString()}
                        />
                    </View>
                    
                )
            default: return <></>
        }
    }

    return (
        <View>
            <FlatList
                data={sections}
                key={`leaders_sections`}
                keyExtractor={item => item}
                renderItem={renderSections}
                refreshing={loading}
                onRefresh={() => {
                    if(!category_id || !timerange_id){ return console.log('') }
                    getLeaders(category_id, timerange_id, 0)
                }}
            />
        </View>
    )
}

export default Leaderboard